/**
 * The booking blocks (CHANGE 2026-10-18 §1): search (full | compact), results, basket. Each renders from the
 * ONE shared state (booking.js) and sends the guest's actions to it; none calls the API.
 */
import { h, clear, html } from './dom.js';
import { createCalendar } from './calendar.js';
import { createGuests } from './guests.js';
import { cardModels, renderCard, renderOffLine } from './cards.js';
import { rateSummary, cancelText } from './rate-summary.js';
import { readTexts } from './texts.js';
import { nightsBetween } from './dates.js';
import { bookingUrl, datesLabel, datesShort, datesSummary } from './stay-ui.js';

const MOBILE = '(max-width: 639px)';

/** After the departure is chosen, the dates section collapses this much later (date-picker change, §7). */
const COLLAPSE_MS = 350;

const ICON_CALENDAR = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="3.5" y="5" width="17" height="15" rx="2"/><path d="M3.5 10h17M8 3v4M16 3v4"/></svg>';
const ICON_GUESTS = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="10" r="3"/><path d="M6.5 18.5c1.3-2.2 3.2-3.3 5.5-3.3s4.2 1.1 5.5 3.3"/></svg>';
const ICON_PROMO = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9"/><path d="M8.5 15.5l7-7"/><circle cx="9" cy="9" r="1"/><circle cx="15" cy="15" r="1"/></svg>';
const ICON_CHEVRON = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 9l6 6 6-6"/></svg>';

/** Summary row code (`totals.rows`, Phase 3) → [label key, CSS class]. */
const SUMMARY_ROWS = {
	ROOMS: ['ui.rooms', ''],
	TOURIST_TAX: ['ui.touristTax', ''],
	TOTAL: ['ui.total', 'is-total'],
	AVERAGE_PER_NIGHT: ['ui.averagePerNight', ''],
	PAY_NOW: ['ui.payNow', 'is-strong'],
	BALANCE: ['ui.balance', '']
};

/**
 * Picker that adapts to the screen: inline calendar (desktop, full search), popover (desktop, compact), or a
 * full-screen sheet behind a button (mobile). onOpen runs before the first display (lazy start, compact).
 * `trigger`: an outside button that opens the sheet / popover (the dates section header) instead of the
 * picker's own.
 */
function createDatePicker(ctx, { inline, onOpen = () => {}, trigger: external = null, month = null }) {
	const { booking } = ctx;
	const mq = window.matchMedia ? window.matchMedia(MOBILE) : { matches: false, addEventListener() {} };
	const wrap = h('div', { class: 'pap-picker' });
	let trigger = external || h('button', { type: 'button', class: 'pap-dates-trigger', 'aria-haspopup': 'dialog', 'aria-expanded': 'false', on: { click: () => open() } });
	let cal = null;
	let variant = null;
	let isOpen = false;
	const listeners = new Set();

	function wanted() {
		return mq.matches ? 'sheet' : inline ? 'inline' : 'popover';
	}

	function build() {
		variant = wanted();
		cal = createCalendar({
			booking,
			i18n: ctx.i18n,
			variant,
			month,
			onClose: variant === 'inline' ? null : () => close(),
			onDone: variant === 'inline' ? null : () => close()
		});
		clear(wrap);
		if (variant === 'inline') {
			wrap.append(cal.el);
			isOpen = true;
		} else {
			if (!external) {
				wrap.append(trigger);
			}
			if (isOpen) {
				wrap.append(cal.el);
			}
		}
	}

	/** Popover: a click outside closes it. */
	function outside(e) {
		if (isOpen && variant === 'popover' && !wrap.contains(e.target) && !trigger.contains(e.target)) {
			close(false);
		}
	}

	function open() {
		onOpen();
		booking.calendarUsed(); // Matomo `calendrier-ouvert` (1.8.0): the sheet / popover opened
		isOpen = true;
		trigger.setAttribute('aria-expanded', 'true');
		wrap.append(cal.el);
		document.body.classList.toggle('pap-sheet-open', variant === 'sheet');
		document.addEventListener('pointerdown', outside);
		cal.focus();
	}

	function close(focusTrigger = true) {
		isOpen = false;
		trigger.setAttribute('aria-expanded', 'false');
		cal.el.remove();
		document.body.classList.remove('pap-sheet-open');
		document.removeEventListener('pointerdown', outside);
		if (focusTrigger) {
			trigger.focus();
		}
	}

	mq.addEventListener?.('change', () => {
		if (wanted() !== variant) {
			const wasOpen = isOpen && variant !== 'inline';
			isOpen = wasOpen;
			build();
			update();
			listeners.forEach((fn) => fn());
		}
	});

	function update() {
		if (!external) {
			clear(trigger).append(datesLabel(ctx));
		}
		if (variant === 'inline' || isOpen) {
			cal.update();
		}
	}

	build();
	return {
		el: wrap,
		update,
		open,
		/** An outside button opens the sheet / popover from now on (the Dates panel header). */
		useTrigger(button) {
			external = button;
			trigger.remove();
			trigger = button;
		},
		onVariant: (fn) => listeners.add(fn),
		get variant() { return variant; },
		get isOpen() { return isOpen; }
	};
}

// ---------------------------------------------------------------- search block (full; compact = compact.js)

/**
 * Full search block. With a results block on the page: the search bar (0.9.9). Without one (a page that sends the
 * guest to the booking page, `target`): the stacked sections and « Rechercher » of 0.9.7, unchanged.
 */
export function mountSearch(node, ctx, opts) {
	return ctx.has('results') ? mountSearchBar(node, ctx, opts) : mountSearchForm(node, ctx, opts);
}

/**
 * Search bar (search-bar change, 19 Oct 2026): three tabs — Voyageurs | Dates | Code promo — and one panel open
 * at a time under them (Dates on load). No « Rechercher »: a search starts on a deliberate action when the form
 * changed (booking.searchIfChanged): a departure picked; Voyageurs closed (« Valider », its tab, another tab,
 * Escape, a click outside); Code promo applied or closed. Opening another tab with such a change searches and
 * keeps the new panel open; every other search closes the panels (0.9.7).
 *
 * Pinned bar: the block itself is sticky (a sticky element cannot leave its parent, and the results block is a
 * sibling), with `top` = --pap-bar-top minus what sits above the bar (the error: hidden while stuck). A panel
 * opened while the bar is stuck drops over the results (.is-overlay) instead of pushing them down.
 */
function mountSearchBar(node, ctx, { month = null }) {
	const { booking, i18n, msg } = ctx;
	const s = booking.state;
	const mq = window.matchMedia ? window.matchMedia(MOBILE) : { matches: false, addEventListener() {} };
	const guests = createGuests({ booking, i18n, msg });
	const uid = Math.random().toString(36).slice(2, 7);
	const agesSet = () => s.childrenAges.every((age) => age !== null);

	function tab(id, icon, text) {
		const title = h('span', { class: 'pap-stab-title' }, text);
		const sum = h('span', { class: 'pap-stab-sum' });
		const short = h('span', { class: 'pap-stab-short' });
		const button = h('button', { type: 'button', class: 'pap-stab pap-stab--' + id, 'aria-expanded': 'false', 'aria-controls': 'pap-sp-' + id + '-' + uid, on: { click: () => switchTo(open === id ? null : id) } },
			html('span', 'pap-stab-ico', icon),
			h('span', { class: 'pap-stab-t' }, title, sum, short),
			html('span', 'pap-stab-chev', ICON_CHEVRON));
		return { id, button, sum, short, title };
	}

	const calendar = () => createCalendar({ booking, i18n, variant: 'inline', month, months: mq.matches ? 1 : null });
	let cal = calendar();
	const agesNote = h('p', { class: 'pap-note pap-warn pap-ages-missing', role: 'alert', hidden: true }, i18n.t('ui.agesMissing'));
	const promoInput = h('input', { type: 'text', autocomplete: 'off', id: 'pap-promo-' + uid, on: {
		input: (e) => { booking.setPromo(e.target.value); render(); },
		keydown: (e) => { if (e.key === 'Enter') { e.preventDefault(); switchTo(null); } }
	} });
	const tabs = {
		guests: tab('guests', ICON_GUESTS, i18n.t('ui.guestsLegend')),
		dates: tab('dates', ICON_CALENDAR, i18n.t('ui.datesTitle')),
		promo: tab('promo', ICON_PROMO, i18n.t('ui.promoTitle'))
	};
	const panel = (id, ...body) => h('div', { class: 'pap-spanel pap-spanel--' + id, id: 'pap-sp-' + id + '-' + uid, hidden: true }, ...body);
	const panels = {
		guests: panel('guests', guests.el, agesNote,
			h('div', { class: 'pap-panel-actions' }, h('button', { type: 'button', class: 'pap-btn', on: { click: validateGuests } }, i18n.t('ui.validate')))),
		dates: panel('dates', cal.el),
		promo: panel('promo', h('div', { class: 'pap-promo' },
			h('label', { class: 'pap-sr', for: promoInput.id }, i18n.t('ui.promo')),
			promoInput,
			h('button', { type: 'button', class: 'pap-btn', on: { click: () => switchTo(null) } }, i18n.t('ui.apply'))))
	};
	const sbar = h('div', { class: 'pap-sbar', on: { keydown: onEscape } },
		tabs.guests.button, tabs.dates.button, tabs.promo.button, panels.guests, panels.dates, panels.promo);
	const error = h('div', { class: 'pap-search-error' });
	// No heading of its own (1.5.1): the page's H1 (CrocoBuilder) says it.
	const form = h('form', { class: 'pap-search pap-search--bar', novalidate: true, on: { submit: (e) => e.preventDefault() } },
		error,
		sbar);
	let open = 'dates'; // the Dates panel is open on load
	let overlay = false; // the open panel drops over the results (opened while the bar was stuck)
	let agesError = false;
	let keepOpen = null; // panel left open by the search that starts now (another tab was opened)
	let wasSearching = s.searching;
	let departureTimer = null;
	let panelRequest = s.panelRequest;

	/**
	 * Opens `next` (null = none), closing the open panel. Closing Voyageurs or Code promo — or Dates while a picked
	 * departure waits for its search — confirms the change: search when the form changed. reveal = false when a
	 * panel stays open or Escape gave the focus back to the tab (the results neither scroll nor take the focus).
	 */
	function switchTo(next, { reveal = next === null } = {}) {
		const was = open;
		const pending = departureTimer !== null;
		clearTimeout(departureTimer);
		departureTimer = null;
		agesError = false;
		if (was === next) {
			return;
		}
		if (next === 'dates') {
			booking.calendarUsed(); // Matomo `calendrier-ouvert` (1.8.0): the Dates panel opened by the guest
		}
		const inside = was && panels[was].contains(document.activeElement);
		if ((was === 'guests' || was === 'promo' || pending) && booking.needsSearch()) {
			keepOpen = next;
			overlay = next ? stuck : false;
			booking.searchIfChanged({ reveal });
			return; // closeOnSearch() opens keepOpen and renders
		}
		open = next;
		overlay = next ? stuck : false;
		render();
		if (inside && !next) {
			tabs[was].button.focus();
		}
	}

	/** « Valider »: a missing age keeps the panel open with the note; no dates yet → the Dates panel. */
	function validateGuests() {
		if (!agesSet()) {
			agesError = true;
			render();
			return;
		}
		switchTo(s.arrival && s.departure ? null : 'dates');
	}

	function onEscape(e) {
		if (e.key === 'Escape' && open) {
			e.preventDefault();
			const id = open;
			tabs[id].button.focus();
			switchTo(null, { reveal: false });
		}
	}

	/** A click outside the bar (tabs + panel) closes the open panel. */
	document.addEventListener('pointerdown', (e) => {
		if (open && !sbar.contains(e.target) && document.contains(e.target)) {
			switchTo(null);
		}
	});

	/**
	 * Departure picked in the calendar (a click or Enter / Space on a day): search a little later (the range is
	 * seen first, COLLAPSE_MS); no search to make → the Dates panel just closes. Detected around the calendar.
	 */
	let before = null;
	const watchPick = { capture: true };
	for (const type of ['click', 'keydown']) {
		panels.dates.addEventListener(type, () => { before = s.arrival + '|' + s.departure; }, watchPick);
		panels.dates.addEventListener(type, () => {
			if (s.arrival && s.departure && s.arrival + '|' + s.departure !== before) {
				clearTimeout(departureTimer);
				departureTimer = setTimeout(departurePicked, COLLAPSE_MS);
			}
		});
	}

	function departurePicked() {
		departureTimer = null;
		if (open !== 'dates') {
			return;
		}
		const inside = panels.dates.contains(document.activeElement);
		if (!booking.searchIfChanged()) {
			open = null;
			render();
			if (inside) {
				tabs.dates.button.focus();
			}
		}
	}

	/**
	 * A search starts: every panel closes (0.9.7), except the one just opened (keepOpen). Focus inside a closing
	 * panel moves to the results title.
	 */
	function closeOnSearch() {
		if (s.searching && !wasSearching) {
			clearTimeout(departureTimer);
			departureTimer = null;
			const inside = Object.values(panels).some((p) => p.contains(document.activeElement));
			open = keepOpen;
			keepOpen = null;
			if (inside && !open) {
				// After every block has rendered this change (the results block shows its title while searching).
				setTimeout(() => document.querySelector('.pap-block--results .pap-results-title')?.focus({ preventScroll: true }), 0);
			}
		}
		wasSearching = s.searching;
	}

	function render() {
		for (const t of Object.values(tabs)) {
			const isOpen = open === t.id;
			t.button.classList.toggle('is-open', isOpen);
			t.button.setAttribute('aria-expanded', String(isOpen));
			panels[t.id].hidden = !isOpen;
		}
		sbar.classList.toggle('is-overlay', Boolean(open) && overlay);
		agesNote.hidden = !agesError;

		const children = s.childrenAges.length;
		clear(tabs.guests.sum).append(
			i18n.t('ui.adultsCount', [s.adults], s.adults),
			i18n.sep(' – '),
			i18n.t('ui.childrenCount', [children], children === 0 ? 2 : children));
		clear(tabs.guests.short).append(i18n.t('ui.partyShort', [s.adults, children]));
		clear(tabs.dates.sum).append(datesSummary(ctx, { short: true }));
		clear(tabs.dates.short).append(...datesShort(ctx));
		const code = promoInput.value.trim().toUpperCase();
		clear(tabs.promo.sum).append(code ? i18n.value(code) : i18n.t('ui.promoAdd'));
		clear(tabs.promo.short).append(code ? i18n.sep('✓') : '');
		tabs.promo.button.classList.toggle('has-code', Boolean(code));
		for (const t of Object.values(tabs)) {
			// The whole summary (the tab shows it cut, or the short form on a phone).
			t.button.setAttribute('aria-label', i18n.s('ui.ariaPair', [t.title.textContent, t.sum.textContent]));
		}
		measure();
	}

	// ------------------------------------------------------------ pinned bar

	let stuck = false;
	let frame = 0;

	/**
	 * --pap-sbar-offset (block): what sits above the bar, so that only the bar stays in view when the block sticks.
	 * --pap-sbar-h (html): the bar's height without an open panel, for the results' scroll-margin-top.
	 * --pap-stabs-h (bar): the tabs' height, to cap an open panel to the window.
	 */
	function measure() {
		if (node.hidden || !node.isConnected) {
			return;
		}
		const top = node.getBoundingClientRect().top;
		const bar = sbar.getBoundingClientRect();
		const cs = getComputedStyle(sbar);
		const tabsH = tabs.dates.button.getBoundingClientRect().height;
		node.style.setProperty('--pap-sbar-offset', Math.max(0, Math.round(bar.top - top)) + 'px');
		sbar.style.setProperty('--pap-stabs-h', Math.round(tabsH) + 'px');
		document.documentElement.style.setProperty('--pap-sbar-h', Math.round(tabsH + parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom)) + 'px');
		// Stuck = the block sits at its sticky `top` (resolved in px) after a scroll.
		const isStuck = window.scrollY > 0 && top <= (parseFloat(getComputedStyle(node).top) || 0) + 0.5;
		if (isStuck !== stuck) {
			stuck = isStuck;
			sbar.classList.toggle('is-stuck', stuck);
			node.classList.toggle('is-stuck', stuck);
		}
	}

	const onScroll = () => {
		if (!frame) {
			frame = requestAnimationFrame(() => {
				frame = 0;
				measure();
			});
		}
	};
	window.addEventListener('scroll', onScroll, { passive: true });
	window.addEventListener('resize', onScroll, { passive: true });

	mq.addEventListener?.('change', () => {
		// Phone ↔ desktop: one month or two.
		const fresh = calendar();
		cal.el.replaceWith(fresh.el);
		cal = fresh;
		cal.update();
		render();
	});

	function update() {
		node.hidden = s.view !== 'search'; // summary, details, retry (0.10.0)
		clear(error);
		if (s.error) {
			error.append(h('div', { class: 'pap-panel pap-error', role: 'alert' }, msg.errorFor(s.error)));
		}
		guests.update();
		closeOnSearch();
		if (agesError && agesSet()) {
			agesError = false;
		}
		if (s.panelRequest !== panelRequest) {
			// « Nouvelle recherche » (resume note): the form is back to its defaults, the Dates panel opens.
			panelRequest = s.panelRequest;
			open = 'dates';
			overlay = false;
			render();
			cal.update();
			tabs.dates.button.focus();
			return;
		}
		render();
		cal.update();
	}

	/**
	 * The bar's full-width background = the page's (the first opaque ancestor: white on chateaupapounan.fr), unless
	 * --pap-sbar-bg is set in the CSS.
	 */
	function pageBackground() {
		if (getComputedStyle(node).getPropertyValue('--pap-sbar-bg').trim()) {
			return;
		}
		for (let a = node.parentElement; a; a = a.parentElement) {
			const bg = getComputedStyle(a).backgroundColor;
			if (bg && bg !== 'transparent' && !/^rgba\(.*,\s*0\)$/.test(bg)) {
				node.style.setProperty('--pap-sbar-bg', bg);
				return;
			}
		}
	}

	node.classList.add('has-sbar');
	pageBackground();
	clear(node).append(form);
	booking.subscribe(update);
	update();
	return { update };
}

/** The stacked sections and « Rechercher » (0.9.2–0.9.7): a full search block on a page without results block. */
function mountSearchForm(node, ctx, { target, room, month = null }) {
	const { booking, i18n, msg } = ctx;
	const s = booking.state;
	const guests = createGuests({ booking, i18n, msg });

	/**
	 * One bordered accordion panel of the prototype (`.acc`): icon, title, summary line, chevron. Only one panel
	 * is open at a time; the Dates header opens the full-screen sheet on a phone instead.
	 */
	function panel(id, icon, title, body) {
		const summary = h('span', { class: 'pap-acc-sum' });
		const bodyId = 'pap-' + id + '-' + Math.random().toString(36).slice(2, 7);
		const header = h('button', { type: 'button', class: 'pap-acc-h', 'aria-controls': bodyId, on: { click: () => toggle(id) } },
			html('span', 'pap-acc-ico', icon),
			h('span', { class: 'pap-acc-t' }, h('span', { class: 'pap-acc-title' }, title), summary),
			html('span', 'pap-acc-chev', ICON_CHEVRON));
		const content = h('div', { class: 'pap-acc-b', id: bodyId }, body);
		return { id, header, summary, body: content, el: h('section', { class: 'pap-acc pap-acc--' + id }, header, content) };
	}

	const picker = createDatePicker(ctx, { inline: true, onOpen: () => booking.start(), trigger: null, month });
	const promoInput = h('input', { type: 'text', autocomplete: 'off', on: { input: (e) => booking.setPromo(e.target.value) } });
	const dates = panel('dates', ICON_CALENDAR, i18n.t('ui.datesTitle'), picker.el);
	const party = panel('guests', ICON_GUESTS, i18n.t('ui.guestsLegend'), guests.el);
	const promo = panel('promo', ICON_PROMO, i18n.t('ui.promoTitle'),
		h('label', { class: 'pap-field pap-promo' }, h('span', {}, i18n.t('ui.promo')), promoInput));
	dates.el.classList.add('pap-dates');
	picker.useTrigger(dates.header);
	const panels = [dates, party, promo];
	const submit = h('button', { type: 'submit', class: 'pap-btn pap-search-submit' }, i18n.t('ui.search'));
	const error = h('div', { class: 'pap-search-error' });
	const form = h('form', { class: 'pap-search', novalidate: true, on: { submit: (e) => { e.preventDefault(); go(); } } },
		error,
		h('div', { class: 'pap-accs' }, dates.el, party.el, promo.el),
		h('div', { class: 'pap-search-actions' }, submit));
	let open = 'dates'; // the Dates panel is open on load
	let lastDeparture = s.departure;
	let collapseTimer = null;
	let wasSearching = s.searching;

	function toggle(id) {
		if (id === 'dates' && picker.variant !== 'inline') {
			picker.open();
			return;
		}
		clearTimeout(collapseTimer);
		open = open === id ? null : id;
		renderPanels();
	}

	function renderPanels() {
		const inline = picker.variant === 'inline';
		for (const p of panels) {
			const sheet = p === dates && !inline;
			const isOpen = !sheet && open === p.id;
			p.el.classList.toggle('is-open', isOpen);
			p.el.classList.toggle('is-sheet', sheet);
			p.body.hidden = !sheet && !isOpen;
			if (sheet) {
				p.header.setAttribute('aria-haspopup', 'dialog');
				p.header.setAttribute('aria-expanded', String(picker.isOpen));
			} else {
				p.header.removeAttribute('aria-haspopup');
				p.header.setAttribute('aria-expanded', String(isOpen));
			}
		}
		clear(dates.summary).append(datesSummary(ctx));
		clear(party.summary).append(
			i18n.t('ui.adultsCount', [s.adults], s.adults),
			i18n.sep(' – '),
			i18n.t('ui.childrenCount', [s.childrenAges.length], s.childrenAges.length === 0 ? 2 : s.childrenAges.length));
		clear(promo.summary);
		if (promoInput.value.trim()) {
			promo.summary.append(i18n.value(promoInput.value.trim().toUpperCase()));
		}
	}

	/** Departure chosen → Dates closes a little later and Voyageurs opens (prototype); departure dropped → Dates again. */
	function followDates() {
		if (s.departure === lastDeparture) {
			return;
		}
		clearTimeout(collapseTimer);
		if (s.arrival && s.departure && picker.variant === 'inline' && open === 'dates') {
			collapseTimer = setTimeout(() => {
				const inside = dates.body.contains(document.activeElement);
				open = 'guests';
				renderPanels();
				if (inside) {
					party.header.focus();
				}
			}, COLLAPSE_MS);
		} else if (s.arrival && !s.departure && lastDeparture) {
			open = 'dates';
		}
		lastDeparture = s.departure;
	}

	/**
	 * Every search (« Rechercher » or the URL preselection) closes Dates, Voyageurs and Code promo; their headers keep
	 * the summary lines (0.9.7). Focus inside a closing section moves to the results title.
	 */
	function closeOnSearch() {
		if (s.searching && !wasSearching) {
			clearTimeout(collapseTimer);
			const inside = panels.some((p) => p.body.contains(document.activeElement));
			open = null;
			if (inside) {
				// After every block has rendered this change (the results block shows its title while searching).
				setTimeout(() => document.querySelector('.pap-block--results .pap-results-title')?.focus({ preventScroll: true }), 0);
			}
		}
		wasSearching = s.searching;
	}

	function go() {
		// A page without results block and with a target: go to the booking page with the choices.
		if (!ctx.has('results') && target) {
			if (s.arrival && s.departure) {
				ctx.navigate(bookingUrl(target, s, room));
			}
			return;
		}
		booking.runSearch();
	}

	function update() {
		node.hidden = s.view !== 'search'; // summary, details, retry (0.10.0)
		clear(error);
		if (s.error) {
			error.append(h('div', { class: 'pap-panel pap-error', role: 'alert' }, msg.errorFor(s.error)));
		}
		guests.update();
		followDates();
		closeOnSearch();
		renderPanels();
		picker.update();
		submit.disabled = !booking.canSearch() || s.searching;
	}

	promoInput.addEventListener('input', renderPanels);
	picker.onVariant(renderPanels);
	clear(node).append(form);
	booking.subscribe(update);
	update();
	return { update };
}

// ---------------------------------------------------------------- results block

export function mountResults(node, ctx) {
	const { booking, i18n, msg, display } = ctx;
	const s = booking.state;
	const open = new Set(); // rooms whose collapsed rates are open
	const more = new Set(); // rooms whose information is expanded (« Afficher plus »)
	const photo = new Map(); // room → photo shown (1.9.2: the arrows), kept across re-renders
	let signature = '';
	const texts = readTexts(document); // room and Beds24 texts printed by PHP (0.9.3), translated server-side

	function render() {
		const stale = booking.isStale();
		const sig = JSON.stringify([s.view, s.searching, s.search?.status, s.search?.body?.searchToken, s.chosen, s.shown, s.order, stale, booking.staleReason(), s.resume]);
		if (sig === signature) {
			return;
		}
		signature = sig;
		node.hidden = s.view !== 'search' || (!s.search && !s.searching);
		// Keep focus on the button the guest pressed (cards are re-rendered).
		const active = document.activeElement;
		const keep = node.contains(active) ? {
			room: active.closest('[data-room]')?.dataset.room,
			offer: active.closest('[data-offer]')?.dataset.offer,
			cls: ['pap-rate-pick', 'pap-rate-more'].find((c) => active.classList.contains(c)) || 'pap-rate-pick'
		} : null;

		const wrap = h('section', { class: 'pap-results' + (stale ? ' is-stale' : ''), 'aria-busy': s.searching ? 'true' : 'false' });
		if (s.resume?.returning && !s.searching) {
			// A search restored on a later visit (resume-search change): when it was saved, and « Nouvelle recherche ».
			wrap.append(h('p', { class: 'pap-note pap-resume-note' },
				i18n.t('ui.resumeNote', [i18n.date(new Date(s.resume.savedAt).toLocaleDateString('en-CA', { timeZone: i18n.zone() }), 'dayMonth')]),
				i18n.sep(' · '),
				h('button', { type: 'button', class: 'pap-link pap-resume-new', on: { click: () => booking.newSearch() } }, i18n.t('ui.newSearchShort')),
				s.resume.lost ? [h('br'), i18n.t('ui.resumeSelectionLost')] : null));
		}
		// The title is there while searching too: a search started from a closing section moves the focus to it (0.9.7).
		wrap.append(h('h2', { class: 'pap-results-title', tabindex: '-1' }, i18n.t('ui.resultsTitle')));
		if (s.searching) {
			wrap.append(h('p', { class: 'pap-loading', role: 'status' }, i18n.t('ui.searching')));
		}
		if (stale && !s.searching) {
			// Why, for the search bar (0.9.9): departure to choose, a child's age missing, or a change to confirm.
			const reason = booking.staleReason();
			const key = reason === 'departure' ? 'ui.resultsStaleDeparture' : reason === 'ages' ? 'ui.resultsStaleAges' : 'ui.resultsStale';
			wrap.append(h('p', { class: 'pap-note pap-warn pap-stale-note', role: 'status' }, i18n.t(key)));
		}
		const r = s.search;
		if (r && r.status !== 200) {
			wrap.append(h('div', { class: 'pap-panel pap-error', role: 'alert' }, msg.errorFor(r)));
		} else if (r) {
			const body = r.body;
			const list = h('div', { class: 'pap-list' + (s.searching ? ' is-hidden' : '') });
			if (body.promo) {
				list.append(h('p', { class: 'pap-note ' + (body.promo.valid ? 'pap-ok' : 'pap-warn') },
					body.promo.valid
						? i18n.promoBanner(body.promo.label) ?? i18n.t('ui.promoCode', [body.promo.code]) // what the code is, by its Beds24 type (1.4.12)
						: body.promo.reason === 'PROMO_UNAVAILABLE_ONLINE' ? msg.withContact('promo.UNAVAILABLE_ONLINE') : i18n.t('promo.INVALID')));
			}
			if (body.hint?.code === 'MIN_ROOMS') {
				list.append(h('p', { class: 'pap-note pap-hint' }, i18n.t('hint.MIN_ROOMS', [body.hint.party, body.hint.minRooms])));
			} else if (body.hint?.code === 'NOT_ENOUGH_BEDS') {
				list.append(h('p', { class: 'pap-note pap-warn pap-hint' }, i18n.t('hint.NOT_ENOUGH_BEDS', [body.hint.party, body.hint.beds])));
			}
			// Rate names printed in the page (« Affichage › Tarifs », translated server-side), else the Beds24 name.
			const rateName = (id) => (i18n.has('rate.name.' + id) ? i18n.s('rate.name.' + id) : null);
			// « Affichage » text (button label) printed in the page labels when set (translated server-side).
			const admin = (key) => (i18n.has('display.' + key) ? i18n.s('display.' + key) : '');
			const shown = { ...display, ctaLabel: admin('ctaLabel') };
			const models = cardModels(s, shown, rateName, texts);
			const card = (model) => renderCard(model, { i18n, msg, layout: display.layout, open, more, photo, first: model === models[0], stale, actions: booking });
			// The requested room unavailable (1.7.0): a banner at the top (before the summary when nothing is bookable);
			// its card goes with the other unavailable ones, first of them (server order, 1.7.1), still highlighted.
			const asked = models.find((m) => m.requested && !m.bookable) || null;
			if (asked) {
				list.append(requestedNote(asked, body.stay, models.some((m) => m.bookable)));
			}
			if (body.summary) {
				list.append(h('div', { class: 'pap-panel pap-summary-msg', role: 'alert' },
					h('p', {}, msg.reason('reason', body.summary)),
					msg.suggestions(body.summary.suggestions, (a, d, type) => booking.chooseSuggestion(a, d, type)),
					msg.contactLine(body.summary.contact)));
			}
			// Server order (bookable first, 0.9.4; the requested room first of its group, 1.7.0–1.7.1) in two groups: the
			// unavailable ones under their own heading (0.9.7), one line each (1.9.0).
			const bookable = models.filter((m) => m.bookable);
			const off = models.filter((m) => !m.bookable);
			// « Également disponibles » (1.9.0): the requested room is bookable (first, server order) and another room too.
			const split = bookable.length > 1 && bookable[0].requested ? 1 : 0;
			if (split) {
				list.append(h('div', { class: 'pap-cards' }, card(bookable[0])),
					h('h3', { class: 'pap-results-off-title pap-results-also-title' }, i18n.t('ui.resultsAlsoAvailable')));
			}
			if (bookable.length > split) {
				list.append(h('div', { class: 'pap-cards' }, ...bookable.slice(split).map(card)));
			}
			if (off.length) {
				list.append(h('h3', { class: 'pap-results-off-title' }, i18n.t('ui.resultsUnavailableTitle')),
					h('div', { class: 'pap-cards pap-cards--off' }, ...off.map((m) => renderOffLine(m, { i18n, msg }))));
			}
			wrap.append(list);
		}
		clear(node).append(wrap);

		if (keep) {
			const sel = keep.offer ? `[data-room="${keep.room}"] [data-offer="${keep.offer}"] .${keep.cls}` : keep.room ? `[data-room="${keep.room}"] input:checked` : null;
			(sel && node.querySelector(sel))?.focus();
		}
	}

	/**
	 * « Suite Océane n'est pas disponible du 10 au 13 mars. » (1.7.0), then the reason of the explanation layer when
	 * it has one, then (1.7.1) « D'autres chambres sont disponibles pour ces dates, ci-dessous. » when a room is
	 * bookable. The room name is the one printed in the page (never translated); the dates by Intl.
	 */
	function requestedNote(m, stay, others) {
		const sameMonth = stay.arrival.slice(0, 7) === stay.departure.slice(0, 7);
		const from = i18n.date(stay.arrival, sameMonth ? 'dayNumber' : 'short');
		const note = h('div', { class: 'pap-panel pap-requested-msg', role: 'status' },
			h('p', { class: 'pap-requested-title' }, i18n.t('ui.requestedUnavailable', [m.name, from, i18n.date(stay.departure, 'short')])));
		if (m.reason?.code && m.reason.code !== 'UNKNOWN') {
			note.append(h('p', { class: 'pap-requested-reason' }, msg.reason('reason', m.reason)));
		}
		if (others) {
			note.append(h('p', { class: 'pap-requested-others' }, i18n.t('ui.requestedRoomOthersAvailable')));
		}
		return note;
	}

	let wasSearching = false;
	booking.subscribe(() => {
		render();
		// A search just ended: bring the top of the results into view — the title, then the requested room's card (first
		// when bookable) or its banner (1.7.2: no longer the card itself, which left the title under the pinned bar) —
		// not when the search bar kept a panel open or Escape gave the focus back to a tab (reveal = false, 0.9.9).
		if (wasSearching && !s.searching && s.search && s.reveal !== false) {
			node.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
			node.querySelector('.pap-results-title')?.focus({ preventScroll: true });
		}
		wasSearching = s.searching;
	});
	render();
}

// ---------------------------------------------------------------- basket block

/** History entry of « Vos coordonnées et paiement » (0.10.0): Back returns to the results, Forward reopens it. */
const DETAILS_HASH = '#coordonnees';

/**
 * Basket block: the selection bar, then (0.10.0) « Vos coordonnées et paiement » when the online booking switch is on
 * (/start config.booking.enabled) — a lazy chunk (checkout.js), prefetched when the bar appears — or today's
 * summary with the disabled « Payer » when it is off. Back from Stripe without paying (?paiement=annule&ref&t):
 * the retry view of that booking.
 */
export function mountBasket(node, ctx) {
	const { booking, i18n, msg } = ctx;
	const s = booking.state;
	const texts = readTexts(node); // room names, cheque payee and address printed by PHP (0.10.0), read before clearing
	const bar = h('div', { class: 'pap-bar', hidden: true });
	const summary = h('section', { class: 'pap-panel pap-summary', hidden: true });
	const checkout = h('div', { class: 'pap-checkout-root', hidden: true });
	clear(node).append(bar, summary, checkout);
	let chunk = null;
	let afterBack = null;
	const cancelLine = (tags) => cancelText(i18n, rateSummary(tags).cancel); // « Votre séjour », same line as the rate card
	const loadCheckout = () => (chunk ??= import('./checkout.js').then((m) => m.mountCheckout(checkout, { ...ctx, texts, leave, cancelLine })));

	/** Leaves the details step for the results (Back in the history when we pushed the entry), then runs `then`. */
	function leave(then = null) {
		if (window.history.state?.pap === 'details' && window.location.hash === DETAILS_HASH) {
			afterBack = then;
			window.history.back();
			return;
		}
		booking.backToResults();
		then?.();
	}

	// A reload on #coordonnees starts from the results (the search is not restored), without an error.
	if (window.location.hash === DETAILS_HASH) {
		window.history.replaceState(null, '', window.location.pathname + window.location.search);
	}
	window.addEventListener('popstate', () => {
		const onDetails = window.location.hash === DETAILS_HASH;
		if (!onDetails && s.view === 'details') {
			booking.backToResults();
			const then = afterBack;
			afterBack = null;
			then?.();
		} else if (onDetails && s.view === 'search' && booking.bookingEnabled() && booking.canContinue()) {
			booking.showSummary(); // Forward: the details step again, with what was typed (draft)
			loadCheckout();
		}
	});

	// Back from Stripe without paying: the booking exists, the store is empty (checkout change §6).
	const p = ctx.params;
	if (p?.get('paiement') === 'annule' && p.get('ref') && p.get('t')) {
		booking.showRetry(p.get('ref'), p.get('t'));
		const url = new URL(window.location.href);
		url.searchParams.delete('ref');
		url.searchParams.delete('t');
		window.history.replaceState(null, '', url.pathname + url.search + url.hash);
		loadCheckout();
	}

	/** The details step shows (« Continuer », or restored after a language switch): its history entry and chunk. */
	function detailsShown() {
		if (s.view !== 'details') {
			return;
		}
		loadCheckout(); // the step scrolls to its top and focuses its title when it opens
		if (window.location.hash !== DETAILS_HASH) {
			window.history.pushState({ pap: 'details' }, '', window.location.pathname + window.location.search + DETAILS_HASH);
		}
	}

	/** « Continuer », or the store continuing by itself (1.5.0): the view change shows the step (subscriber). */
	function continueClicked() {
		booking.showSummary();
	}

	/** Today's summary (switch off) shows: scroll to it and focus its title, whichever way it opened. */
	let lastView = s.view;
	function summaryShown() {
		if (s.view === 'summary' && lastView !== 'summary') {
			node.scrollIntoView?.({ block: 'start' });
			summary.querySelector('h2')?.focus({ preventScroll: true });
		}
		lastView = s.view;
	}

	let chosenOnce = false; // the bar waits for the guest's first choice on this page (1.9.0), then behaves as before

	function renderBar() {
		// Hidden when nothing can be booked, and while the results are stale (0.9.7).
		const bookable = s.view === 'search' && s.search?.status === 200 && !s.searching
			&& s.search.body.rooms.some((r) => r.bookable) && !booking.isStale();
		if (bookable && booking.bookingEnabled()) {
			loadCheckout(); // prefetch the details step while the guest chooses
		}
		chosenOnce ||= s.order.length > 0;
		const visible = bookable && chosenOnce;
		bar.hidden = !visible;
		if (!visible) {
			return;
		}
		const q = s.quote;
		const placed = q?.status === 200 ? q.body.allocation.placed : 0;
		const status = q?.status === 200 ? q.body.allocation.status : null; // the server's answer (Allocation)
		const party = booking.guests();
		clear(bar).append(h('div', { class: 'pap-bar-in' },
			h('div', { class: 'pap-bar-t' },
				h('strong', {}, i18n.t('ui.basket')),
				h('span', { class: 'pap-counter' + (status === 'OK' ? ' pap-ok' : '') }, i18n.t('ui.guestsPlaced', [placed, party], party)),
				s.order.length === 0 ? h('span', {}, i18n.t('ui.basketEmpty')) : null,
				s.quoting ? h('span', { role: 'status' }, i18n.t('ui.updating')) : null,
				q && q.status !== 200 ? h('span', { class: 'pap-warn', role: 'alert' }, msg.errorFor(q)) : null,
				status === 'NOT_ENOUGH_ROOMS' ? h('span', { class: 'pap-warn' }, i18n.t('allocation.NOT_ENOUGH_ROOMS', [q.body.allocation.missing], q.body.allocation.missing)) : null,
				status === 'NOT_ENOUGH_ADULTS' ? h('span', { class: 'pap-warn' }, i18n.t('allocation.NOT_ENOUGH_ADULTS')) : null,
				status === 'TOO_MANY_ROOMS' ? h('span', { class: 'pap-warn' }, i18n.t('allocation.TOO_MANY_ROOMS')) : null,
				q?.status === 200 ? h('span', { class: 'pap-total' }, i18n.t('ui.totalAmount', [i18n.money(q.body.totals.total)])) : null),
			h('button', {
				type: 'button',
				class: 'pap-btn',
				disabled: !booking.canContinue(),
				on: { click: continueClicked }
			}, i18n.t('ui.continue'))));
	}

	function renderSummary() {
		summary.hidden = s.view !== 'summary';
		if (summary.hidden) {
			clear(summary);
			return;
		}
		const q = s.quote.body;
		// Room and rate names from the page (CheckoutTexts, the labels list), never from the API (01 §13).
		const rateName = (id) => (i18n.has('rate.name.' + id) ? i18n.t('rate.name.' + id) : '');
		const row = (key, amount, cls = '') => h('div', { class: 'pap-row ' + cls }, h('span', {}, i18n.t(key)), i18n.value(i18n.money(amount)));
		// The code (1.4.7, as « Votre séjour »): « Code X » + the amount, the text of its Beds24 type under it
		// (i18n.promoLabel, 1.4.8; none without a label); without a code name, « Réduction ».
		const discountRow = (promo, amount) => {
			const text = i18n.promoLabel(promo?.label);
			return h('div', { class: 'pap-row is-discount' },
				h('span', {}, promo?.code ? i18n.t('ui.promoCode', [promo.code]) : i18n.t('ui.discount'), text ? h('small', {}, text) : null),
				i18n.value(i18n.money(amount)));
		};
		const nights = nightsBetween(s.arrival, s.departure);
		clear(summary).append(
			h('button', { type: 'button', class: 'pap-link', on: { click: () => booking.backToResults() } }, h('span', { 'aria-hidden': 'true' }, '‹ '), i18n.t('ui.back')),
			h('h2', { tabindex: '-1' }, i18n.t('ui.summaryTitle')),
			h('p', {}, msg.stayRange(s.arrival, s.departure, nights), i18n.sep(' · '), i18n.t('ui.guests', [booking.guests()], booking.guests())),
			...q.lines.map((line) => h('div', { class: 'pap-line' },
				h('h3', { 'data-no-translation': true }, texts.text('room.' + line.roomId + '.name') ?? ''), // never translated (0.9.11)
				h('p', {}, rateName(line.offerId)),
				h('p', {}, i18n.t('ui.occupancy', [line.occupancy], line.occupancy), i18n.sep(' · '), i18n.t('ui.nights', [line.nights], line.nights)),
				// Before the code (1.4.7, as « Votre séjour »): the code is its own row in the totals.
				h('div', { class: 'pap-row' }, h('span', {}, i18n.t('ui.rooms')), i18n.value(i18n.money(line.totalBeforePromo ?? line.total))),
				h('div', { class: 'pap-row' }, h('span', {}, i18n.t('ui.touristTax')), i18n.value(i18n.money(line.touristTax))))),
			...q.notices.map((n) => h('p', { class: 'pap-note' }, i18n.t('notice.' + n.code))),
			// The server's rows (Phase 3): which ones, in which order, with which sign — never decided here.
			h('div', { class: 'pap-totals' }, ...(q.totals.rows || []).map((r) => r.code === 'DISCOUNT' ? discountRow(q.promo, r.amount)
				: SUMMARY_ROWS[r.code] ? row(SUMMARY_ROWS[r.code][0], r.amount, SUMMARY_ROWS[r.code][1]) : null)),
			h('button', { type: 'button', class: 'pap-btn', disabled: true }, i18n.t('ui.pay')),
			h('p', { class: 'pap-note' }, i18n.t('ui.payComingSoon')));
	}

	booking.subscribe(() => {
		detailsShown();
		renderBar();
		renderSummary();
		summaryShown();
		// CrocoBuilder content around the blocks can hide itself on the details step (customisation.md).
		document.body.classList.toggle('pap-step-details', s.view === 'details' || s.view === 'retry');
	});
	renderBar();
}
